/**
 * Screen 1c, the creator's half — the questions a learner is asked at the end.
 *
 * Every course starts with the template questions; a teacher reorders them,
 * switches them off, or adds their own. The two switches are the design's:
 * *Ask for feedback at the end* and *Show me who said what*.
 *
 * ── Why this one page batches its edits ─────────────────────────────────────
 * The rest of the app writes on every change. `PUT /courses/:id/feedback-form`
 * states the whole form, because reordering questions is not expressible as a
 * patch of any one of them — so a per-keystroke write would send the entire
 * form on every letter typed into a label. The draft lives here until **Save**,
 * and leaving with it unsaved changes nothing that learners see.
 */

import { useState } from 'react';
import type { FeedbackFormWire, FeedbackQuestionKind, FeedbackQuestionWire } from '@wire';
import { errorText, learn, teach } from '../api';
import { ErrorNote, Footnote, Loading, OkNote, PageTitle, Panel, PanelHead } from '../components/Chrome';
import { ds } from '../ds';
import { useRouter } from '../router';
import { HAIR, monoPlain, plural } from '../theme';
import { useLoad } from '../useLoad';

const KINDS: { value: FeedbackQuestionKind; label: string }[] = [
  { value: 'scale', label: 'Scale of 1 to 5' },
  { value: 'text', label: 'Long text' },
  { value: 'choice', label: 'One of a few' }
];

/** Client-side, and replaced by the server if it wants its own. An id only has
 *  to be stable within one form. */
const newId = () => `q_${Math.random().toString(36).slice(2, 10)}`;

export default function FeedbackForm({ id }: { id: string }) {
  const { Button, IconButton, Input, Select, Switch } = ds();
  const { navigate } = useRouter();

  const { data, error, loading, reload } = useLoad(
    () => teach.feedbackForm.get(id),
    'Could not load the feedback form.',
    id
  );
  const course = useLoad(() => learn.course(id), 'Could not load this course.', id);

  const [draft, setDraft] = useState<FeedbackFormWire | null>(null);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  /** The **Options** textarea's own text, by question id — see the note beside
   *  its `onChange` below for why `q.options` cannot be that value directly. */
  const [optionText, setOptionText] = useState<Record<string, string>>({});

  if (error) return <ErrorNote>{error}</ErrorNote>;
  if (!data) return loading ? <Loading /> : null;

  const form = draft ?? data;
  const edit = (next: Partial<FeedbackFormWire>) => {
    setOk(null);
    setDraft({ ...form, ...next });
  };

  const editQuestion = (index: number, next: Partial<FeedbackQuestionWire>) => {
    const questions = form.questions.map((q, i) => (i === index ? { ...q, ...next } : q));
    edit({ questions });
  };

  const move = (index: number, by: number) => {
    const target = index + by;
    if (target < 0 || target >= form.questions.length) return;
    const questions = [...form.questions];
    const moved = questions[index]!;
    questions[index] = questions[target]!;
    questions[target] = moved;
    edit({ questions });
  };

  const save = async () => {
    setBusy(true);
    setFailed(null);
    try {
      // The textarea keeps every blank line while it is being typed into —
      // see `optionText` — so a real option list is only cut here, once,
      // rather than on every keystroke.
      const cleaned: FeedbackFormWire = {
        ...form,
        questions: form.questions.map((q) =>
          q.kind === 'choice'
            ? { ...q, options: (q.options ?? []).map((o) => o.trim()).filter(Boolean) }
            : q
        )
      };
      await teach.feedbackForm.save(id, cleaned);
      setDraft(null);
      setOptionText({});
      await reload();
      setOk('The form is saved.');
    } catch (err) {
      setFailed(errorText(err, 'The form did not save.'));
    }
    setBusy(false);
  };

  return (
    <>
      <PageTitle title="Feedback form." meta={course.data?.course.title ?? null}>
        <Button variant="ghost" size="sm" onClick={() => navigate(`/teach/courses/${id}/report`)}>
          Report
        </Button>
        <Button variant="primary" size="sm" disabled={busy || !draft} onClick={() => void save()}>
          Save
        </Button>
      </PageTitle>

      <ErrorNote>{failed}</ErrorNote>
      <OkNote>{ok}</OkNote>

      <Panel style={{ display: 'grid', gap: 14 }}>
        <Switch
          label="Ask for feedback at the end"
          checked={form.enabled}
          disabled={busy}
          onChange={(next) => edit({ enabled: next })}
        />
        <Switch
          label="Show me who said what"
          checked={form.named}
          disabled={busy}
          onChange={(next) => edit({ named: next })}
        />
      </Panel>

      <Panel style={{ display: 'grid', gap: 16 }}>
        <PanelHead title="Questions" meta={plural(form.questions.length, 'question')}>
          <Button
            variant="secondary"
            size="sm"
            disabled={busy}
            onClick={() =>
              edit({
                questions: [
                  ...form.questions,
                  {
                    id: newId(),
                    kind: 'text',
                    label: '',
                    required: false,
                    template: false
                  }
                ]
              })
            }
          >
            Add a question
          </Button>
        </PanelHead>

        {form.questions.map((q, i) => (
          <div
            key={q.id}
            style={{ display: 'grid', gap: 10, borderTop: HAIR, paddingTop: 14 }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={monoPlain}>{q.template ? `Question ${i + 1} · template` : `Question ${i + 1}`}</span>
              <span style={{ marginLeft: 'auto', display: 'flex', gap: 4 }}>
                <IconButton
                  icon="ArrowUp"
                  label="Move up"
                  size="sm"
                  disabled={busy || i === 0}
                  onClick={() => move(i, -1)}
                />
                <IconButton
                  icon="ArrowDown"
                  label="Move down"
                  size="sm"
                  disabled={busy || i === form.questions.length - 1}
                  onClick={() => move(i, 1)}
                />
                <IconButton
                  icon="Trash2"
                  label="Remove this question"
                  size="sm"
                  disabled={busy}
                  onClick={() => edit({ questions: form.questions.filter((_, j) => j !== i) })}
                />
              </span>
            </div>

            <Input
              label="Question"
              value={q.label}
              disabled={busy}
              onChange={(e) => editQuestion(i, { label: e.target.value })}
            />

            <div style={{ display: 'flex', gap: 14, alignItems: 'flex-end', flexWrap: 'wrap' }}>
              <Select
                label="Answer"
                size="sm"
                value={q.kind}
                style={{ minWidth: 190 }}
                disabled={busy}
                onChange={(e) => editQuestion(i, { kind: e.target.value as FeedbackQuestionKind })}
                options={KINDS}
              />
              <Switch
                label="Required"
                checked={q.required}
                disabled={busy}
                onChange={(next) => editQuestion(i, { required: next })}
              />
            </div>

            {q.kind === 'choice' && (
              <Input
                label="Options"
                multiline
                rows={3}
                hint="One option per line"
                // `q.options` is not the value here: filtering blank lines on
                // every keystroke deletes the newline a person just typed
                // before they can type the option after it, so pressing Enter
                // after the first option could never start a second one. The
                // textarea keeps its own text verbatim; only `save()` trims
                // and drops the blanks.
                value={optionText[q.id] ?? (q.options ?? []).join('\n')}
                disabled={busy}
                onChange={(e) => {
                  const text = e.target.value;
                  setOptionText((t) => ({ ...t, [q.id]: text }));
                  editQuestion(i, { options: text.split('\n') });
                }}
              />
            )}
          </div>
        ))}

        {form.questions.length === 0 && <Footnote>Add the first question.</Footnote>}
      </Panel>
    </>
  );
}
